import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Building2, KeyRound, Lock, Search, Shield, Zap } from 'lucide-react';
import { getUsersPaginated, getUserEffectivePermissions, type User, type EffectivePermission } from '../../packages/api-client/pm-api';
import { apiClient, getErrorMessage } from '../../packages/api-client';
import { useAuth } from '../../packages/auth-client';
import { Badge, Dialog, PageHeader, StateView, Table, type Column } from '../../packages/ui';
import { usePageTitle } from '../../hooks/usePageTitle';
import './UsersPage.css';

/**
 * Platform › Users — the superadmin's cross-tenant directory (U29). Every row
 * carries its tenant. Tenant people administration (invites with scope) lives
 * in People & Access (U24a); /admin/users sends everyone else there
 * (platformRoutes), and this page refuses to render for them too.
 */

interface Tenant { id: string; name: string }
interface Role { id: string; name: string }
interface PendingInvite {
    id: string; email: string; first_name: string; last_name: string;
    expires_at: string; created_at: string;
    is_project_restricted?: boolean; is_task_restricted?: boolean;
}

/** D4: the only assignable tenant roles (the server rejects the rest). */
const TENANT_ROLES = ['admin', 'user'];
const FETCH_LIMIT = 100; // the backend's pagination.MaxLimit
export const MAX_PAGES = 50; // 5,000 users; past that the list says it is partial

type Load<T> = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; data: T };

/** Fetches on mount and on reload(); failure is an explicit error state, never an empty list. */
function useLoad<T>(fetch: () => Promise<T>): [Load<T>, () => void, React.Dispatch<React.SetStateAction<Load<T>>>] {
    const [state, setState] = useState<Load<T>>({ status: 'loading' });
    const reload = useCallback(() => {
        setState({ status: 'loading' });
        fetch().then((data) => setState({ status: 'ready', data }))
            .catch((e) => setState({ status: 'error', message: getErrorMessage(e) }));
    }, [fetch]);
    useEffect(reload, [reload]);
    return [state, reload, setState];
}

/**
 * The whole directory, page by page, so the tenant filter and search are over
 * every user, not the current page. The superadmin list has no server-side
 * tenant or search filter on main.
 * ponytail: client-side over the full list; switch to GET /users?tenant_id=&search=
 * (PR #334) with server paging once the directory is in the thousands.
 */
interface Directory { users: User[]; partial: boolean; total?: number }

async function fetchAllUsers(maxPages: number): Promise<Directory> {
    const byId = new Map<string, User>();
    for (let page = 1; page <= maxPages; page++) {
        const r = await getUsersPaginated(page, FETCH_LIMIT);
        // The list is ordered by created_at only, so a tie can straddle pages.
        (r.data ?? []).forEach((u) => byId.set(u.id, u));
        const users = [...byId.values()];
        // Without a total there is no telling whether more exist.
        const total = r.meta?.total;
        if (typeof total !== 'number') return { users, partial: true };
        if ((r.data ?? []).length < FETCH_LIMIT || page * FETCH_LIMIT >= total) return { users, partial: false, total };
        if (page === maxPages) return { users, partial: true, total };
    }
    return { users: [], partial: true }; // unreachable: the loop always returns
}

const fetchTenants = () => apiClient.get<{ data: Tenant[] }>('/tenants').then((r) => r.data.data ?? []);
const fetchRoles = () => apiClient.get<{ data: Role[] }>('/roles')
    .then((r) => (r.data.data ?? []).filter((x) => TENANT_ROLES.includes(x.name)).sort((a, b) => a.name.localeCompare(b.name)));
// null = 403: no invite permission in the operator's own tenant.
const fetchInvites = () => apiClient.get<{ data: PendingInvite[] }>('/user-invitations').then((r) => r.data.data ?? [])
    .catch((e) => {
        if ((e as { response?: { status?: number } })?.response?.status === 403) return null;
        throw e;
    });

const fullName = (u: { first_name?: string; last_name?: string; email?: string }) =>
    `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email || '—';
const date = (s?: string) => (s ? new Date(s).toLocaleDateString() : '—');

/** maxPages is a test seam; production always uses MAX_PAGES. */
export function UsersPage({ maxPages = MAX_PAGES }: { maxPages?: number } = {}) {
    usePageTitle('Users');
    const { isSuperAdmin, user } = useAuth();
    if (!isSuperAdmin()) {
        return (
            <StateView state="denied" reason="The cross-tenant user directory is for platform superadmins."
                nextStep={<>Manage your workspace's people in <Link to="/people">People &amp; access</Link>.</>} />
        );
    }
    return <PlatformUsers ownTenantId={user?.tenant_id} maxPages={maxPages} />;
}

type Pending = { kind: 'deactivate' | 'reactivate'; user: User } | { kind: 'cancel-invite'; invite: PendingInvite };

function PlatformUsers({ ownTenantId, maxPages }: { ownTenantId?: string; maxPages: number }) {
    const fetchUsers = useCallback(() => fetchAllUsers(maxPages), [maxPages]);
    const [users, reloadUsers, setUsers] = useLoad(fetchUsers);
    const [tenants, reloadTenants] = useLoad(fetchTenants);
    const [roles] = useLoad(fetchRoles);
    const [invites, reloadInvites, setInvites] = useLoad(fetchInvites);

    const [params, setParams] = useSearchParams();
    const tenantFilter = params.get('tenant') ?? '';
    const q = params.get('q') ?? '';
    const setParam = (key: string, value: string) => setParams((p) => {
        const next = new URLSearchParams(p);
        if (value) next.set(key, value); else next.delete(key);
        return next;
    }, { replace: true });

    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState<User | null>(null);
    const [pending, setPending] = useState<Pending | null>(null);

    const tenantList = tenants.status === 'ready' ? tenants.data : [];
    const tenantName = (id?: string) => (id ? tenantList.find((t) => t.id === id)?.name : undefined);
    const roleList = roles.status === 'ready' ? roles.data : [];

    const patchUser = (id: string, patch: Partial<User>) => setUsers((s) => s.status === 'ready'
        ? { ...s, data: { ...s.data, users: s.data.users.map((u) => (u.id === id ? { ...u, ...patch } : u)) } } : s);

    const all = users.status === 'ready' ? users.data.users : [];
    const needle = q.trim().toLowerCase();
    const shown = all.filter((u) => (!tenantFilter || u.tenant_id === tenantFilter) && (!needle
        || [fullName(u), u.email, tenantName(u.tenant_id) ?? u.tenant_id ?? ''].some((s) => s.toLowerCase().includes(needle))));

    const tenantCell = (id?: string) => {
        if (!id) return <span className="pu-muted">No tenant</span>;
        const name = tenantName(id);
        return <span className="pu-tenant"><Building2 size={12} aria-hidden="true" /> {name ?? <code>{id}</code>}</span>;
    };

    const cols: Column<User & Record<string, unknown>>[] = [
        { key: 'name', header: 'Name', rowHeader: true, render: (u) => fullName(u) },
        { key: 'email', header: 'Email' },
        { key: 'tenant', header: 'Tenant', render: (u) => tenantCell(u.tenant_id) },
        {
            key: 'role', header: 'Role', render: (u) => (
                <span className="pu-badges">
                    {u.is_superadmin && <Badge variant="warning"><Zap size={11} aria-hidden="true" /> superadmin</Badge>}
                    {(u.roles ?? []).map((r) => <Badge key={r}>{r}</Badge>)}
                    {!u.is_superadmin && !(u.roles ?? []).length && <span className="pu-muted">No role</span>}
                </span>
            ),
        },
        {
            key: 'access', header: 'Access', render: (u) => (u.is_project_restricted || u.is_task_restricted)
                ? <span className="pu-badges">
                    {u.is_project_restricted && <Badge variant="warning"><Lock size={11} aria-hidden="true" /> Project-restricted</Badge>}
                    {u.is_task_restricted && <Badge variant="warning"><Lock size={11} aria-hidden="true" /> Task-restricted</Badge>}
                </span>
                : <span className="pu-muted">Full workspace</span>,
        },
        { key: 'otp', header: 'OTP', render: (u) => u.otp_exempt ? <Badge variant="warning">OTP exempt</Badge> : <span className="pu-muted">OTP required</span> },
        { key: 'status', header: 'Status', render: (u) => <Badge variant={u.is_active ? 'success' : 'danger'}>{u.is_active ? 'Active' : 'Inactive'}</Badge> },
        {
            key: 'actions', header: 'Actions', render: (u) => (
                <span className="pu-actions">
                    <button type="button" className="btn btn-ghost btn-sm" aria-label={`Edit ${fullName(u)}`} onClick={() => setEditing(u)}>Edit</button>
                    <button type="button" className={`btn btn-ghost btn-sm ${u.is_active ? 'pu-danger' : ''}`}
                        aria-label={`${u.is_active ? 'Deactivate' : 'Reactivate'} ${fullName(u)}`}
                        onClick={() => setPending({ kind: u.is_active ? 'deactivate' : 'reactivate', user: u })}>
                        {u.is_active ? 'Deactivate' : 'Reactivate'}
                    </button>
                </span>
            ),
        },
    ];

    const inviteCols: Column<PendingInvite & Record<string, unknown>>[] = [
        { key: 'email', header: 'Email', rowHeader: true },
        { key: 'name', header: 'Name', render: (i) => `${i.first_name} ${i.last_name}`.trim() || '—' },
        {
            key: 'status', header: 'Status', render: (i) => new Date(i.expires_at).getTime() < Date.now()
                ? <Badge variant="danger">Expired</Badge>
                : <span className="pu-muted">Sent {date(i.created_at)} · expires {date(i.expires_at)}</span>,
        },
        {
            key: 'actions', header: 'Actions', render: (i) => (
                <span className="pu-actions">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => resendInvite(i)}>Resend</button>
                    <button type="button" className="btn btn-ghost btn-sm pu-danger" onClick={() => setPending({ kind: 'cancel-invite', invite: i })}>Cancel</button>
                </span>
            ),
        },
    ];

    async function resendInvite(inv: PendingInvite) {
        try {
            await apiClient.post(`/user-invitations/pending/${inv.id}/resend`);
            toast.success('Invitation email resent');
            reloadInvites();
        } catch (e) { toast.error(getErrorMessage(e)); }
    }

    async function runPending(p: Pending) {
        if (p.kind === 'cancel-invite') {
            await apiClient.delete(`/user-invitations/pending/${p.invite.id}`);
            setInvites((s) => s.status === 'ready' && s.data ? { ...s, data: s.data.filter((i) => i.id !== p.invite.id) } : s);
            toast.success('Invitation cancelled');
            return;
        }
        // Flip the flag rather than DELETE: a soft-deleted user vanishes from every list.
        const is_active = p.kind === 'reactivate';
        const { data } = await apiClient.put<User>(`/users/${p.user.id}`, { is_active });
        patchUser(p.user.id, { ...(data ?? {}), is_active });
    }

    const filterName = tenantName(tenantFilter);
    const subtitle = users.status !== 'ready' ? 'Every user on the platform, with their tenant.'
        : tenantFilter || needle
            ? `${shown.length} of ${all.length} users${tenantFilter ? ` in ${filterName ?? tenantFilter}` : ''}`
            : `${all.length} user${all.length === 1 ? '' : 's'} across ${new Set(all.map((u) => u.tenant_id)).size} tenants`;

    const directory = () => {
        if (users.status === 'loading') return <StateView state="loading" label="Loading users" />;
        if (users.status === 'error') return <StateView state="error" title="Users couldn't be loaded" message={users.message} onRetry={reloadUsers} />;
        if (all.length === 0) return <StateView state="empty" title="No users yet" message="Create the first user in a tenant." />;
        if (shown.length === 0) {
            return <StateView state="filtered-empty" title="No users match these filters" onClearFilters={() => setParams({}, { replace: true })} />;
        }
        return <Table caption="Users" hideCaption stickyHeader columns={cols} data={shown as (User & Record<string, unknown>)[]} rowKey={(u) => u.id} />;
    };
    const partialNotice = users.status === 'ready' && users.data.partial
        ? `Showing the first ${all.length}${users.data.total !== undefined ? ` of ${users.data.total}` : ''} users. Filters and search cover only these.`
        : null;

    return (
        <div className="pu-page">
            <PageHeader
                breadcrumb={[{ label: 'Platform' }, { label: 'Users' }]}
                title="Users"
                subtitle={subtitle}
                actions={
                    <button type="button" className="btn btn-primary btn-md" onClick={() => setCreating(true)}
                        disabled={tenants.status !== 'ready'} title={tenants.status === 'ready' ? undefined : 'Needs the tenant list'}>
                        New user
                    </button>
                }
            />

            <div className="pu-filters" role="search">
                <div className="form-group">
                    <label className="form-label" htmlFor="pu-tenant">Tenant</label>
                    <select id="pu-tenant" className="form-input" value={tenantFilter} onChange={(e) => setParam('tenant', e.target.value)}>
                        <option value="">All tenants</option>
                        {tenantList.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        {tenantFilter && !tenantList.some((t) => t.id === tenantFilter) && <option value={tenantFilter}>{tenantFilter}</option>}
                    </select>
                </div>
                <div className="form-group pu-search">
                    <label className="form-label" htmlFor="pu-q">Search users</label>
                    <span className="pu-search-field">
                        <Search size={14} aria-hidden="true" />
                        <input id="pu-q" className="form-input" type="search" placeholder="Name, email or tenant" value={q} onChange={(e) => setParam('q', e.target.value)} />
                    </span>
                </div>
            </div>

            {tenants.status === 'error' && (
                <div role="alert" className="pu-notice">
                    Tenant names couldn't be loaded ({tenants.message}); tenant IDs are shown instead.
                    <button type="button" className="btn btn-secondary btn-sm" onClick={reloadTenants}>Retry</button>
                </div>
            )}

            {partialNotice
                ? <StateView state="partial" message={partialNotice} onRetry={reloadUsers}>{directory()}</StateView>
                : directory()}

            <section className="pu-section" aria-label="Pending invitations">
                <h2 className="pu-section-title">Pending invitations</h2>
                <p className="pu-muted pu-hint">
                    Invitations belong to one tenant. These are for your own tenant, {tenantName(ownTenantId) ?? <code>{ownTenantId ?? 'none'}</code>};
                    each tenant's admins manage theirs in People &amp; access.
                </p>
                {invites.status === 'loading' && <StateView state="loading" label="Loading invitations" />}
                {invites.status === 'error' && <StateView state="error" title="Invitations couldn't be loaded" message={invites.message} onRetry={reloadInvites} />}
                {invites.status === 'ready' && invites.data === null && <p className="pu-muted">You can't manage invitations in your tenant.</p>}
                {invites.status === 'ready' && invites.data && (invites.data.length === 0
                    ? <StateView state="empty" title="No pending invitations" />
                    : <Table caption="Pending invitations" hideCaption columns={inviteCols}
                        data={invites.data as (PendingInvite & Record<string, unknown>)[]} rowKey={(i) => i.id} />)}
            </section>

            {creating && (
                <CreateDialog tenants={tenantList} roles={roleList} rolesError={roles.status === 'error'} initialTenant={tenantFilter}
                    onClose={() => setCreating(false)} onCreated={() => { setCreating(false); reloadUsers(); }} />
            )}
            {editing && (
                <EditDialog user={editing} tenant={tenantCell(editing.tenant_id)} roles={roleList} rolesError={roles.status === 'error'}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); reloadUsers(); }}
                    onOtpChanged={(exempt) => { patchUser(editing.id, { otp_exempt: exempt }); setEditing({ ...editing, otp_exempt: exempt }); }} />
            )}
            {pending && <PendingConfirm pending={pending} onClose={() => setPending(null)} run={runPending} />}
        </div>
    );
}

// ─── Confirmations ───────────────────────────────────────────────────────────

function ConfirmDialog({ title, body, confirmLabel, danger, onConfirm, onClose }: {
    title: string; body: React.ReactNode; confirmLabel: string; danger?: boolean;
    onConfirm: () => Promise<void>; onClose: () => void;
}) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    async function confirm() {
        setBusy(true); setError(null);
        try { await onConfirm(); onClose(); }
        catch (e) { setError(getErrorMessage(e)); setBusy(false); }
    }
    return (
        <Dialog open onClose={onClose} title={title} footer={<>
            <button type="button" className="btn btn-secondary btn-md" onClick={onClose}>Cancel</button>
            <button type="button" className={`btn btn-${danger ? 'danger' : 'primary'} btn-md`} disabled={busy} onClick={confirm}>{confirmLabel}</button>
        </>}>
            <div className="pu-form">
                {error && <p role="alert" className="pu-error">{error}</p>}
                {body}
            </div>
        </Dialog>
    );
}

function PendingConfirm({ pending, onClose, run }: { pending: Pending; onClose: () => void; run: (p: Pending) => Promise<void> }) {
    const go = () => run(pending);
    if (pending.kind === 'cancel-invite') {
        return <ConfirmDialog title={`Cancel the invitation to ${pending.invite.email}?`} confirmLabel="Cancel invitation" danger
            body={<p>The invite link stops working.</p>} onConfirm={go} onClose={onClose} />;
    }
    const name = fullName(pending.user);
    return pending.kind === 'deactivate'
        ? <ConfirmDialog title={`Deactivate ${name}?`} confirmLabel="Deactivate" danger onConfirm={go} onClose={onClose}
            body={<p>They lose access and are signed out everywhere. Their projects and tasks are kept.</p>} />
        : <ConfirmDialog title={`Reactivate ${name}?`} confirmLabel="Reactivate" onConfirm={go} onClose={onClose}
            body={<p>They can sign in to their tenant again with their existing role.</p>} />;
}

// ─── Create ──────────────────────────────────────────────────────────────────

function RoleSelect({ id, roles, rolesError, value, onChange, legacy, allowNone = true }: {
    id: string; roles: Role[]; rolesError: boolean; value: string; onChange: (v: string) => void;
    /** Creating may leave the role unset; editing replaces it, never removes it. */
    allowNone?: boolean;
    /** The user's current role when it isn't admin|user: shown, never offered. */
    legacy?: Role;
}) {
    if (rolesError) return <p className="pu-error">Roles couldn't be loaded, so the role can't be changed here.</p>;
    return (
        <div className="form-group">
            <label className="form-label" htmlFor={id}>Role</label>
            <select id={id} className="form-input" value={value} onChange={(e) => onChange(e.target.value)}>
                {allowNone ? <option value="">No role assigned</option> : !value && <option value="" disabled>Choose a role</option>}
                {legacy && <option value={legacy.id} disabled>{legacy.name} (legacy, not assignable)</option>}
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <p className="pu-muted pu-hint">The tenant role: admin manages people, roles and billing; user does project work.</p>
        </div>
    );
}

function CreateDialog({ tenants, roles, rolesError, initialTenant, onClose, onCreated }: {
    tenants: Tenant[]; roles: Role[]; rolesError: boolean; initialTenant: string; onClose: () => void; onCreated: () => void;
}) {
    const [f, setF] = useState({ email: '', password: '', first_name: '', last_name: '', tenant_id: initialTenant });
    const [roleId, setRoleId] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setSaving(true); setError(null);
        try {
            const { data } = await apiClient.post<User>('/users', f);
            // The user row exists already; say so rather than implying the role was set.
            if (roleId && data?.id) {
                await apiClient.post(`/users/${data.id}/roles/${roleId}`)
                    .catch((err) => toast.error(`User created, but the role was not assigned: ${getErrorMessage(err)}`));
            }
            toast.success(`Created ${f.email}`);
            onCreated();
        } catch (err) { setError(getErrorMessage(err)); setSaving(false); }
    }

    return (
        <Dialog open onClose={onClose} title="New user" closeOnBackdrop={false}>
            <form onSubmit={submit} className="pu-form">
                {error && <p role="alert" className="pu-error">{error}</p>}
                <p className="pu-muted">Creates the account directly with a password. To invite someone by email, use their tenant's People &amp; access.</p>
                <div className="form-group">
                    <label className="form-label" htmlFor="cr-tenant">Tenant</label>
                    <select id="cr-tenant" className="form-input" required value={f.tenant_id} onChange={(e) => setF({ ...f, tenant_id: e.target.value })}>
                        <option value="">Select a tenant…</option>
                        {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label" htmlFor="cr-first">First name</label>
                        <input id="cr-first" className="form-input" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} />
                    </div>
                    <div className="form-group">
                        <label className="form-label" htmlFor="cr-last">Last name</label>
                        <input id="cr-last" className="form-input" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} />
                    </div>
                </div>
                <div className="form-group">
                    <label className="form-label" htmlFor="cr-email">Email</label>
                    <input id="cr-email" className="form-input" type="email" required autoFocus value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
                </div>
                <div className="form-group">
                    <label className="form-label" htmlFor="cr-password">Password</label>
                    <input id="cr-password" className="form-input" type="password" required minLength={8} autoComplete="new-password"
                        placeholder="At least 8 characters" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
                </div>
                <RoleSelect id="cr-role" roles={roles} rolesError={rolesError} value={roleId} onChange={setRoleId} />
                <div className="pu-form-footer">
                    <button type="button" className="btn btn-secondary btn-md" onClick={onClose}>Cancel</button>
                    <button type="submit" className="btn btn-primary btn-md" disabled={saving}>{saving ? 'Creating…' : 'Create user'}</button>
                </div>
            </form>
        </Dialog>
    );
}

// ─── Edit, effective permissions, OTP ────────────────────────────────────────

function EditDialog({ user, tenant, roles, rolesError, onClose, onSaved, onOtpChanged }: {
    user: User; tenant: React.ReactNode; roles: Role[]; rolesError: boolean;
    onClose: () => void; onSaved: () => void; onOtpChanged: (exempt: boolean) => void;
}) {
    const [f, setF] = useState({
        email: user.email, first_name: user.first_name ?? '', last_name: user.last_name ?? '',
        p: !!user.is_project_restricted, t: !!user.is_task_restricted,
    });
    const [roleId, setRoleId] = useState('');
    const [current, setCurrent] = useState<Load<Role[]>>({ status: 'loading' });
    const origRoleIds = current.status === 'ready' ? current.data.map((x) => x.id) : [];
    const legacyRole = current.status === 'ready' && current.data[0] && !TENANT_ROLES.includes(current.data[0].name) ? current.data[0] : undefined;
    const [perms, setPerms] = useState<Load<EffectivePermission[]>>({ status: 'loading' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmOtp, setConfirmOtp] = useState(false);

    useEffect(() => {
        apiClient.get<{ data: Role[] }>(`/users/${user.id}/roles`).then((r) => {
            const list = r.data.data ?? [];
            setCurrent({ status: 'ready', data: list });
            if (list[0]) setRoleId(list[0].id);
        }).catch((e) => setCurrent({ status: 'error', message: getErrorMessage(e) }));
        getUserEffectivePermissions(user.id)
            .then((data) => setPerms({ status: 'ready', data }))
            .catch((e) => setPerms({ status: 'error', message: getErrorMessage(e) }));
    }, [user.id]);

    const emailChanged = f.email.trim() !== user.email.trim();
    const name = fullName(user);
    const exempt = !!user.otp_exempt;

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setSaving(true); setError(null);
        try {
            // The backend applies an email change's side effects (sign-out, both addresses notified).
            await apiClient.put<User>(`/users/${user.id}`, {
                first_name: f.first_name, last_name: f.last_name,
                is_project_restricted: f.p, is_task_restricted: f.t,
                ...(emailChanged ? { email: f.email.trim() } : {}),
            });
            if (emailChanged) toast.success("Email updated. The user's sessions were signed out and both addresses notified.");
            // One POST: the server replaces the user's single role atomically.
            // Only once the current role is known, so a failed lookup never reassigns.
            if (current.status === 'ready' && roleId && !origRoleIds.includes(roleId)) await apiClient.post(`/users/${user.id}/roles/${roleId}`);
            onSaved();
        } catch (err) { setError(getErrorMessage(err)); setSaving(false); }
    }

    const byRole: Record<string, EffectivePermission[]> = {};
    if (perms.status === 'ready') for (const p of perms.data) (byRole[p.role] ??= []).push(p);

    return (
        <Dialog open onClose={onClose} title={`Edit ${name}`} closeOnBackdrop={false}>
            <form onSubmit={submit} className="pu-form">
                {error && <p role="alert" className="pu-error">{error}</p>}
                <p className="pu-context">Tenant: {tenant}</p>
                <div className="form-group">
                    <label className="form-label" htmlFor="ed-email">Email</label>
                    <input id="ed-email" className="form-input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
                    {emailChanged && <p className="pu-warn">Saving signs this user out everywhere and notifies both addresses.</p>}
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label" htmlFor="ed-first">First name</label>
                        <input id="ed-first" className="form-input" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} />
                    </div>
                    <div className="form-group">
                        <label className="form-label" htmlFor="ed-last">Last name</label>
                        <input id="ed-last" className="form-input" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} />
                    </div>
                </div>
                {current.status === 'loading' && <StateView state="loading" label="Loading the current role" lines={1} />}
                {current.status === 'error' && (
                    <p role="alert" className="pu-error">The current role couldn't be loaded ({current.message}), so it can't be changed here. Other changes still save.</p>
                )}
                {current.status === 'ready' && (
                    <RoleSelect id="ed-role" roles={roles} rolesError={rolesError} value={roleId} onChange={setRoleId} legacy={legacyRole} allowNone={false} />
                )}
                <fieldset className="pu-fieldset">
                    <legend className="form-label">Access scope</legend>
                    <label className="pu-check">
                        <input type="checkbox" checked={f.p} onChange={(e) => setF({ ...f, p: e.target.checked })} />
                        Project-restricted — only sees projects they belong to
                    </label>
                    <label className="pu-check">
                        <input type="checkbox" checked={f.t} onChange={(e) => setF({ ...f, t: e.target.checked })} />
                        Task-restricted — only sees tasks assigned to or created by them
                    </label>
                </fieldset>

                <div className="pu-box">
                    <h3 className="pu-box-title"><KeyRound size={14} aria-hidden="true" /> Sign-in OTP</h3>
                    <p className="pu-muted">{exempt
                        ? 'Exempt: this user skips the one-time code at sign-in.'
                        : 'Required: this user enters a one-time code at sign-in.'}</p>
                    <div>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setConfirmOtp(true)}>
                            {exempt ? 'Require OTP…' : 'Exempt from OTP…'}
                        </button>
                    </div>
                </div>

                <div className="pu-box">
                    <h3 className="pu-box-title"><Shield size={14} aria-hidden="true" /> Effective permissions (as saved)</h3>
                    {perms.status === 'loading' && <StateView state="loading" label="Loading permissions" lines={1} />}
                    {perms.status === 'error' && <p className="pu-error">Permissions couldn't be loaded: {perms.message}</p>}
                    {perms.status === 'ready' && perms.data.length === 0 && <p className="pu-muted">No permissions — assign a role to grant access.</p>}
                    {Object.entries(byRole).map(([role, list]) => (
                        <div key={role}>
                            <p className="pu-perm-role">{role} · {list.length}</p>
                            <ul className="pu-perms" aria-label={`${role} permissions`}>
                                {list.map((p) => <li key={`${p.resource}:${p.action}`}><code>{p.resource}:{p.action}</code></li>)}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="pu-form-footer">
                    <button type="button" className="btn btn-secondary btn-md" onClick={onClose}>Cancel</button>
                    <button type="submit" className="btn btn-primary btn-md" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
                </div>
            </form>

            {confirmOtp && (
                <ConfirmDialog
                    title={exempt ? `Require OTP for ${name}?` : `Exempt ${name} from OTP?`}
                    confirmLabel={exempt ? 'Require OTP' : 'Exempt from OTP'}
                    danger={!exempt}
                    body={exempt
                        ? <p>They will enter a one-time code at their next sign-in.</p>
                        : <p>They will sign in with a password alone. Use this only for service accounts or trusted internal users. This takes effect now, separately from the form.</p>}
                    onConfirm={async () => {
                        await apiClient.patch(`/auth/admin/users/${user.id}/otp-exempt`, { exempt: !exempt });
                        onOtpChanged(!exempt);
                        toast.success(exempt ? 'OTP is required again' : 'OTP exemption set');
                    }}
                    onClose={() => setConfirmOtp(false)} />
            )}
        </Dialog>
    );
}
